import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { toast } from 'sonner';
import { notificationService } from './notificationService';

class PushNotificationService {
  private initialized = false;
  private registrationInProgress = false;

  private formatError(error: unknown): string {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error;
    if (typeof error === 'object') {
      try {
        return JSON.stringify(error);
      } catch {
        return '[object error]';
      }
    }
    return String(error);
  }

  private async createDefaultChannel() {
    try {
      await PushNotifications.createChannel({
        id: 'default',
        name: 'Bildirimler',
        description: 'Genel bildirim kanalı',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
      await PushNotifications.createChannel({
        id: 'weighbridge-alerts',
        name: 'Kantar Uyarıları',
        description: 'Kantar yaklaşım uyarıları için sesli bildirim kanalı',
        importance: 5,
        visibility: 1,
        vibration: true,
        sound: 'default',
      });
    } catch (error) {
      console.warn('Notification channel creation failed:', this.formatError(error));
    }
  }

  async registerCurrentToken() {
    const storedToken = localStorage.getItem('pushToken');
    const authToken = localStorage.getItem('authToken');
    if (storedToken && authToken) {
      try {
        await notificationService.registerPushToken(storedToken, Capacitor.getPlatform());
      } catch (error) {
        console.error('Push token server registration failed:', this.formatError(error));
      }
    }
  }

  async init() {
    if (this.initialized || this.registrationInProgress || !Capacitor.isNativePlatform()) return;

    this.registrationInProgress = true;
    try {
      const permission = await PushNotifications.checkPermissions();
      let receive = permission.receive;

      if (receive !== 'granted') {
        const request = await PushNotifications.requestPermissions();
        receive = request.receive;
      }

      if (receive !== 'granted') {
        console.warn('Push notification permission not granted');
        return;
      }

      await this.createDefaultChannel();
      await PushNotifications.removeAllListeners().catch(() => {});

      PushNotifications.addListener('registration', (token: Token) => {
        console.log('Push registration token:', token.value);
        localStorage.setItem('pushToken', token.value);
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          notificationService.registerPushToken(token.value, Capacitor.getPlatform()).catch((error) => {
            console.error('Push token registration failed:', this.formatError(error));
          });
        }
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.warn('Push registration error (non-blocking):', this.formatError(error));
      });

      PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
        console.log('Push notification received:', notification);
        const title = notification.title || 'Yeni bildirim';
        const body = notification.body || '';
        toast.info(title, {
          description: body,
          duration: 8000,
        });
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
        console.log('Push notification action performed:', notification);
      });

      await PushNotifications.register();
      await this.registerCurrentToken();
      this.initialized = true;
    } catch (error) {
      console.warn('Push init failed (non-blocking):', this.formatError(error));
    } finally {
      this.registrationInProgress = false;
    }
  }
}

export const pushNotificationService = new PushNotificationService();
